import React, { useEffect, useRef, useState } from "react";
import "./time-range-picker.scss";

import { RANGE_OPTIONS } from "./data/defaults";
import { useYsentinel } from "./data/YsentinelDataContext";

const LABELS = {
  "24h": "24h",
  "7d":  "7d",
  "14d": "14d",
  "30d": "30d",
};

const isCustomRange = (r) => r && typeof r === "object" && r.type === "custom";

const formatCustomLabel = (r) => {
  if (!isCustomRange(r)) return "Custom…";
  const days = Math.max(1, Math.round((new Date(r.endDate) - new Date(r.startDate)) / 86_400_000) + 1);
  return `Custom · ${days}d`;
};

const todayIso = () => new Date().toISOString().slice(0, 10);
const isoDaysAgo = (n) => new Date(Date.now() - n * 86_400_000).toISOString().slice(0, 10);

const TimeRangePicker = () => {
  const { range, setRange } = useYsentinel();
  const [open, setOpen] = useState(false);
  const [draftStart, setDraftStart] = useState(isCustomRange(range) ? range.startDate : isoDaysAgo(7));
  const [draftEnd,   setDraftEnd]   = useState(isCustomRange(range) ? range.endDate   : todayIso());
  const popoverRef = useRef(null);

  // Click-outside dismissal for the custom popover.
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const applyCustom = () => {
    if (!draftStart || !draftEnd) return;
    if (new Date(draftEnd) < new Date(draftStart)) return;
    setRange({ type: "custom", startDate: draftStart, endDate: draftEnd });
    setOpen(false);
  };

  const customActive = isCustomRange(range);

  return (
    <div className="ysentinel-range-picker" role="group" aria-label="Time range">
      <span className="ysentinel-range-picker__label">Time range</span>
      <div className="ysentinel-range-picker__pills">
        {RANGE_OPTIONS.map((opt) => (
          <button
            key={opt}
            type="button"
            className={`ysentinel-range-picker__pill${range === opt ? " is-active" : ""}`}
            onClick={() => setRange(opt)}
            aria-pressed={range === opt}
          >
            {LABELS[opt]}
          </button>
        ))}
      </div>
      <div className="ysentinel-range-picker__custom" ref={popoverRef}>
        <button
          type="button"
          className={`ysentinel-range-picker__pill ysentinel-range-picker__pill--standalone${customActive ? " is-active" : ""}`}
          onClick={() => setOpen((v) => !v)}
          aria-pressed={customActive}
          aria-expanded={open}
        >
          {formatCustomLabel(range)}
        </button>
        {open && (
          <div className="ysentinel-range-picker__popover" role="dialog" aria-label="Custom time range">
            <label>
              <span>Start</span>
              <input
                type="date"
                value={draftStart}
                max={draftEnd}
                onChange={(e) => setDraftStart(e.target.value)}
              />
            </label>
            <label>
              <span>End</span>
              <input
                type="date"
                value={draftEnd}
                min={draftStart}
                max={todayIso()}
                onChange={(e) => setDraftEnd(e.target.value)}
              />
            </label>
            <div className="ysentinel-range-picker__popover-actions">
              <button type="button" className="ghost" onClick={() => setOpen(false)}>Cancel</button>
              <button type="button" className="primary" onClick={applyCustom}>Apply</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TimeRangePicker;
